import { Box } from '@chakra-ui/react'
import { forwardRef, useId, useMemo } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

import { fieldErrorMessages, FormField, IFormFieldProps } from '../FormField'
import { IRichTextFont } from './bannerFormats'
import './lineHeight'
import './RichTextField.css'
import { bannerToolbar, RICH_TEXT_TOOLBARS, RichTextToolbar } from './toolbars'

/**
 * Altura do editor quando a tela não diz outra.
 *
 * Os 14 sites traziam seis alturas (100/110/150/200/300px) escritas como `style={{ height }}` no
 * *wrapper*, mais um `marginBottom: '42px'` em cada um — o `42px` era a altura da barra de
 * ferramentas, que ficava de fora da caixa e empurrava o conteúdo seguinte. Aqui a medida vai
 * para a área de edição, que é o que ela sempre quis dizer, e o encosto não é mais necessário.
 */
const DEFAULT_MIN_H = '150px'

export interface IRichTextFieldProps extends Omit<IFormFieldProps, 'children' | 'onChange' | 'htmlFor'> {
  /** O HTML do editor. */
  value: string
  /** Recebe o HTML novo — o valor, não o evento. */
  onChange: (value: string) => void
  /** Vocabulário da barra. Padrão `basic`. */
  toolbar?: RichTextToolbar
  /** Altura mínima da área de edição. A barra fica fora dela. */
  minH?: string
  placeholder?: string
  /** Só leitura (enquanto o formulário salva, por exemplo). */
  isReadOnly?: boolean
  /**
   * Fontes do seletor do token `banner`, já com as enviadas pela aba "Fontes" (`useBannerFonts`).
   * Sem ela, o seletor fica com as três do site.
   */
  fonts?: IRichTextFont[]
}

/**
 * Campo de texto rico do painel — já é um `FormField` por dentro, como o `<ImageCropInput>` e o
 * `<VideoFileInput>`, então `label`/`error`/`isRequired` vão direto nele e **não** se embrulha em
 * outro `FormControl`.
 *
 * Eram 19 `<ReactQuill>` em 11 arquivos: 10 definições locais de `toolbar` em quatro
 * vocabulários (três delas byte a byte iguais), o `import 'react-quill/dist/quill.snow.css'`
 * repetido em 12 arquivos, seis alturas escritas como `style` no wrapper com o mesmo
 * `marginBottom: '42px'` mágico ao lado, e `.ql-editor`/`.ql-toolbar` reestilizados em quatro
 * lugares — um deles num `.css` global de página.
 *
 * - **`toolbar` é um token** (`basic`, `comment`, `full`, `color`, `post`), e `formats` sai do
 *   mesmo registry: a barra e o que o editor aceita ao colar não podem mais divergir.
 * - **`minH` no lugar de `height` + `marginBottom`**: a altura vai para o `.ql-container`.
 * - O CSS do Quill é importado **uma vez**, aqui.
 * - A moldura é a do próprio Quill, com raio e cor do painel. As páginas desenhavam um
 *   `<Box border="1px solid" borderColor="gray.200">` em volta do editor, que já tem borda: a
 *   linha dupla aparecia nas duas telas que faziam as duas coisas.
 *
 * **O rótulo ainda não nomeia o editor.** O `id` do react-quill vai para a `<div class="quill">`
 * externa, que não é um elemento rotulável: o `htmlFor` aponta para ela, clicar no rótulo não
 * foca o campo e o `.ql-editor` fica sem nome acessível. Não é regressão — nenhum dos 19 sites
 * tinha associação —, mas também não está resolvido: falta nomear o `.ql-editor` pelo ref do
 * Quill (`role="textbox"` + `aria-labelledby`), e isso fica para a rodada da acessibilidade.
 *
 * O ref vai para esse contêiner, e é por isso que ele é `tabIndex={-1}`: o `PostsFormModal` rola
 * até o primeiro campo recusado pela API e o foca, e o editor precisa ser focável por código
 * (mas não por Tab, que é da barra e da área de edição).
 */
const RichTextField = forwardRef<HTMLDivElement, IRichTextFieldProps>(function RichTextField(
  { value, onChange, toolbar = 'basic', minH = DEFAULT_MIN_H, placeholder, isReadOnly = false, fonts, error, ...rest },
  ref
) {
  const generatedId = useId()
  const fontsKey = fonts?.map(({ slug }) => slug).join(',')
  // Memorizado pela lista de fontes: o react-quill recria o editor quando `modules` muda de
  // referência, e um objeto novo a cada render perderia a seleção a cada tecla.
  const { modules, formats } = useMemo(
    () => (toolbar === 'banner' && fonts ? bannerToolbar(fonts.map(({ slug }) => slug)) : RICH_TEXT_TOOLBARS[toolbar]),
    [toolbar, fontsKey]
  )
  // A mesma regra do `FormField`, e não uma paralela: com duas, um `error={['']}` pintava a
  // borda de vermelho sem mostrar mensagem nenhuma.
  const borderColor = fieldErrorMessages(error).length > 0 ? 'red.500' : 'gray.200'

  return (
    <FormField error={error} htmlFor={generatedId} {...rest}>
      <Box
        ref={ref}
        tabIndex={-1}
        className={toolbar === 'post' || toolbar === 'banner' ? `rich-text-field-${toolbar}` : undefined}
        sx={{
          // `sticky`: sem altura fixa o editor cresce com o texto e quem rola é o corpo do modal,
          // levando a barra junto — num texto longo ela sai da tela e formatar o fim do documento
          // exige subir o modal inteiro. Era o que o `height` fixo escondia.
          '.ql-toolbar.ql-snow': {
            borderTopRadius: 'md',
            borderColor,
            bg: 'gray.50',
            position: 'sticky',
            top: 0,
            zIndex: 1
          },
          // Toda barra é `sticky` com o mesmo `zIndex`, então a do editor seguinte (outra
          // camada, mais abaixo no DOM) cobria o menu aberto desta — com dois editores
          // empilhados, o fim da lista de fontes ficava inalcançável.
          '.ql-toolbar.ql-snow:has(.ql-expanded)': { zIndex: 2 },
          '.ql-container.ql-snow': { borderBottomRadius: 'md', borderColor, bg: 'white', fontSize: '14px' },
          '.ql-container': { minHeight: minH },
          '.ql-editor': { minHeight: minH }
        }}
      >
        <ReactQuill
          id={generatedId}
          theme="snow"
          value={value}
          // **Só edição de gente grava.** Ao receber o `value` por prop o Quill normaliza o HTML
          // para o que o token aceita e emite um `text-change` de origem `api`: sem esta guarda,
          // abrir um registro já salvo escreveria a versão podada no estado do formulário, e um
          // "Salvar" sem tocar no texto persistiria a perda. O `handler` de um token que formata
          // por conta própria precisa passar `'user'` (ver `toolbars.ts`).
          onChange={(html, _delta, source) => source === 'user' && onChange(html)}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={isReadOnly}
        />
      </Box>
    </FormField>
  )
})

export default RichTextField
